/* eslint-disable */
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col, DatePicker, message, Modal,
  Row,
  Space, Spin,
  Tooltip,
  Typography
} from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import {
  Controller, FormProvider, useFieldArray, useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient
} from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DropdownPayroll from './dropdownPayroll';

import { useAppSelector } from 'app/store';
import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import {
  createProjectService, getProjectByIdService, updateProjectService
} from 'common/services/extends/project';
import { CreateProjectParams } from 'common/services/extends/project/types';
import { getAllStaffFulltimeService } from 'common/services/extends/staffFulltime';
import { DepartmentEnum, OrderByEnum, OrderTypeEnum } from 'common/services/extends/staffFulltime/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError, detectErrorByCode } from 'common/utils/functions';
import { projectSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';

interface ProjectForm {
  name: string;
  code: string;
  customerName: string;
  rangeDate: [string, string];
  account: OptionType;
  operator: OptionType;
  payrolls: Array<{ option: OptionType | undefined, code: string, canDelete?: boolean }>;
}

const defaultValues = {
  name: '',
  code: '',
  customerName: '',
  account: undefined,
  operator: undefined,
  payrolls: [
    {
      option: undefined,
      code: ''
    }
  ],
};

const defaultValuePayroll = {
  option: undefined,
  code: ''
};

const ProgramDetail: React.FC<ActiveRoles> = ({ roleUpdate, roleCreate }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const projectId = searchParams.get('projectId') || '';

  const rolesUser = useAppSelector((state) => state.auth.roles);

  /* States */
  const [searchAC, setSearchAC] = useState('');
  const [searchOP, setSearchOP] = useState('');

  /* React-hook-form */
  const method = useForm<ProjectForm>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(projectSchema),
  });

  const exceptIds = method.watch('payrolls').reduce((acc, cur) => {
    const newAcc = [...acc];
    if (cur.option?.value) newAcc.push(Number(cur.option.value));
    return newAcc;
  }, [] as number[]).join(',');

  const { fields, append, remove } = useFieldArray({
    control: method.control,
    name: 'payrolls',
  });

  const queryKey = ['project-detail', projectId];

  const { isLoading } = useQuery(
    queryKey,
    () => getProjectByIdService(projectId),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        if (data) {
          method.reset({
            name: data.projectData?.name || '',
            code: data.projectData?.code || '',
            customerName: data.projectData?.customerName || '',
            rangeDate: [data.projectData?.fromDate, data.projectData?.toDate],
            account: data.accountant ? {
              label: data.accountant.fullname,
              value: data.accountant.id,
            } : undefined,
            operator: data.operation ? {
              label: data.operation.fullname,
              value: data.operation.id,
            } : undefined,
            payrolls: Array.isArray(data.payrolls) ? data.payrolls?.map((item) => ({
              option: {
                label: item.fullname,
                value: item.id
              },
              code: item.code,
              canDelete: item.canDelete,
            })) : [],
          });
        }
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { mutate: createProjectMutate, isLoading: createLoading } = useMutation(
    ['create-project'],
    async (params: CreateProjectParams) => createProjectService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(`${ROUTE_PATHS.PROGRAM_MANAGEMENT}`);
      },
      onError: (errors: ErrorResponse[]) => {
        const errs = Array.isArray(errors) ? errors : [];
        if (errs.length > 0) {
          errs.forEach((err) => {
            console.log(err.field, err.message, err.code);

            method.setError(
              err.field as keyof ProjectForm,
              { message: t(detectErrorByCode(err.code, err.field, err.message)) }
            );
            message.error(
              t(detectErrorByCode(err.code, err.field, err.message))
            );
          })
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );

  const { mutate: updateProjectMutate, isLoading: updateLoading } = useMutation(
    ['update-project'],
    async (params: CreateProjectParams & { id: string }) => updateProjectService({ ...params, id: params.id }),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: (errors: ErrorResponse[]) => {
        const errs = Array.isArray(errors) ? errors : [];
        if (errs.length > 0) {
          errs.forEach((err) => {
            if (err.code === 'staffPayrollHasPayrollPeriod') {
              message.error(
                `Không thể xoá khi người tạo bảng lương ${err.staffs?.[0].name || ''} đã có kỳ chấm công`
              );
            } else {
              message.error(
                t(detectErrorByCode(err.code, err.field, err.message))
              );
            }
          })
        } else {
          message.error(t('message.updateError'));
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      }
    }
  );

  const { data: accountsRes, isLoading: accountsLoading, hasNextPage: hasNextAccountsPage, fetchNextPage: fetchNextAccounts } = useInfiniteQuery(
    ['get-accounts', searchAC],
    ({ pageParam }) => getAllStaffFulltimeService({
      page: pageParam || 1,
      _department: DepartmentEnum.account,
      _orderBy: OrderByEnum.fullname,
      _orderType: OrderTypeEnum.asc,
      ...searchAC && { keyword: searchAC },
    }),
    {
      getNextPageParam: (lastPage) => (lastPage?.meta?.page >= lastPage?.meta?.totalPages
        ? undefined : lastPage?.meta?.page + 1),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleLoadmoreAccounts = () => {
    if (hasNextAccountsPage) {
      fetchNextAccounts();
    }
  }

  const formatAccounts: OptionType[] = useMemo(() => accountsRes?.pages.reduce((acc, page) => {
    const newAcc = [...acc, ...page.data.map((item) => ({
      label: item.staffFullTimeData.fullname,
      value: item.staffFullTimeData.id
    }))];
    return newAcc;
  }, [] as OptionType[])
    || [], [accountsRes]);

  const { data: opsRes, isLoading: opsLoading, hasNextPage: hasNextOpsPage, fetchNextPage: fetchNextOps } = useInfiniteQuery(
    ['get-operations', searchOP],
    ({ pageParam }) => getAllStaffFulltimeService({
      page: pageParam || 1,
      _department: DepartmentEnum.operation,
      _orderBy: OrderByEnum.fullname,
      _orderType: OrderTypeEnum.asc,
      ...searchOP && { keyword: searchOP },
    }),
    {
      getNextPageParam: (lastPage) => (lastPage?.meta?.page >= lastPage?.meta?.totalPages
        ? undefined : lastPage?.meta?.page + 1),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleLoadmoreOps = () => {
    if (hasNextOpsPage) {
      fetchNextOps();
    }
  }

  const formatOps: OptionType[] = useMemo(() => opsRes?.pages.reduce((acc, page) => {
    const newAcc = [...acc, ...page.data.map((item) => ({
      label: item.staffFullTimeData.fullname,
      value: item.staffFullTimeData.id
    }))];
    return newAcc;
  }, [] as OptionType[]) || [], [opsRes]);

  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) return;

    const formData = method.getValues();

    const submitData: CreateProjectParams = {
      name: formData.name,
      code: formData.code,
      customerName: formData.customerName || '',
      fromDate: dayjs(formData?.rangeDate[0])?.format('YYYY-MM-DD'),
      toDate: dayjs(formData?.rangeDate[1])?.format('YYYY-MM-DD'),
      acId: Number(formData.account?.value),
      opId: Number(formData.operator?.value),
      payrollIds: formData.payrolls.map((item) => Number(item.option?.value))
    };

    if (projectId) {
      updateProjectMutate({
        id: projectId,
        ...submitData
      });
    } else {
      createProjectMutate(submitData);
    }
  };

  const permission = (!!projectId && roleUpdate) || (!projectId && roleCreate);

  /* Render */
  return (
    <div className="p-programDetail">
      <HeaderPage
        fixed
        title={t('sidebar.program')}
        rightHeader={(
          <Space size={16}>
            <Button
              type="primary"
              disabled={(projectId && !roleUpdate) || (!projectId && !roleCreate)}
              loading={updateLoading || createLoading}
              onClick={method.handleSubmit(onSubmit)}
            >
              <SaveOutlined />
              {t('system.save')}
            </Button>
          </Space>
        )}
      />
      <Spin size="large" spinning={isLoading || createLoading || updateLoading}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col>
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Card
                    title={(
                      <Space size={0} style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Typography.Title level={4}>
                          {t('program.info')}
                        </Typography.Title>
                      </Space>
                    )}
                  >
                    <div className="site-card-border-less-wrapper">
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                      >
                        <Row gutter={16}>
                          <Col span={8} className="u-mt-8">
                            <Typography.Text strong>
                              {t('timesheet.codeProgram')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="code"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={!!projectId}
                                  placeholder={t('timesheet.codeProgram')}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mt-8">
                            <Typography.Text strong>
                              {t('program.nameCustomer')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="customerName"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  placeholder={t('program.nameCustomer')}
                                  disabled={!permission}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mt-8">
                            <Typography.Text strong>
                              {t('timesheet.nameProgram')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="name"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  placeholder={t('timesheet.nameProgram')}
                                  disabled={!permission}
                                />
                              )}
                            />
                          </Col>
                          <Col span={24} className="u-mt-8">
                            <Typography.Text strong>
                              {`${t('program.fromDate')} - ${t('program.toDate')}`}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <div className="u-mt-8">
                              <Controller
                                name="rangeDate"
                                control={method.control}
                                render={({
                                  field: { value, onChange },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <DatePicker.RangePicker
                                      value={value ? [dayjs(value[0], 'YYYY-MM-DD'), dayjs(value[1], 'YYYY-MM-DD')] : undefined}
                                      onChange={onChange}
                                      size="large"
                                      format="DD/MM/YYYY"
                                      style={{ width: '100%' }}
                                      disabled={!permission}
                                    />
                                    {error && (
                                      <span
                                        className="a-input_errorMessage"
                                      >
                                        {error?.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </Col>
                          <Col span={12} className="u-mt-8">
                            <Typography.Text strong>
                              {t('program.personInChargeAC')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="account"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <DropdownElement
                                  locale="vi"
                                  options={formatAccounts}
                                  placeholder={t('program.personInChargeAC')}
                                  isGetOption
                                  value={value}
                                  isShowSearch
                                  error={error?.message}
                                  onChange={onChange}
                                  size="large"
                                  loading={accountsLoading}
                                  // slugs={initBankNames?.[index]}
                                  handleLoadMore={handleLoadmoreAccounts}
                                  handleExternalSearch={(val) => setSearchAC(val)}
                                  disabled={!permission}
                                />
                              )}
                            />
                          </Col>
                          <Col span={12} className="u-mt-8">
                            <Typography.Text strong>
                              {t('program.personInChargeOP')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="operator"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <DropdownElement
                                  locale="vi"
                                  options={formatOps}
                                  placeholder={t('program.personInChargeOP')}
                                  isGetOption
                                  value={value}
                                  isValueSlug
                                  isShowSearch
                                  error={error?.message}
                                  onChange={onChange}
                                  size="large"
                                  loading={opsLoading}
                                  // slugs={initBankNames?.[index]}
                                  handleLoadMore={handleLoadmoreOps}
                                  handleExternalSearch={(val) => setSearchOP(val)}
                                  disabled={!permission}
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      </Space>
                    </div>
                  </Card>
                  <Card
                    title={(
                      <Typography.Title level={4}>
                        {t('program.creatorTableSalary')}
                      </Typography.Title>
                    )}
                  >
                    <Col span={24} className="u-mt-8">
                      <Controller
                        name="payrolls"
                        control={method.control}
                        render={({
                          fieldState
                        }) => (
                          <>
                            <table>
                              <thead>
                                <tr>
                                  <th>
                                    {t('program.creator')}
                                    {' '}
                                    <Typography.Text strong type="danger">
                                      *
                                    </Typography.Text>
                                  </th>
                                  <th>
                                    {t('system.action')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {
                                  fields.map((field, index) => (
                                    <tr key={field.id}>
                                      <td>
                                        <Controller
                                          name={`payrolls.${index}.option`}
                                          control={method.control}
                                          render={({
                                            field: { value, onChange },
                                            fieldState: { error },
                                          }) => (
                                            <DropdownPayroll
                                              locale="vi"
                                              placeholder={t('program.creator')}
                                              isGetOption
                                              value={value}
                                              isShowSearch
                                              error={error?.message}
                                              onChange={onChange}
                                              size="large"
                                              exceptIds={exceptIds}
                                              disabled={!permission}
                                            />
                                          )}
                                        />
                                      </td>
                                      <td style={{ textAlign: 'center' }}>
                                        <Space>
                                          <Tooltip title={t('program.seeDetail')}>
                                            <Button
                                              type="text"
                                              disabled={!field.code || !getPermission(rolesUser, roles.SHOW_PAYROLL_PROJECT)}
                                              onClick={() => {
                                                const updatedParams = new URLSearchParams(searchParams);
                                                updatedParams.set('staffFullTimeId', field.option?.value?.toString() || '');
                                                navigate(`${ROUTE_PATHS.PROJECT_TIMESHEET_DETAIL}?${updatedParams.toString()}`);
                                              }}
                                            >
                                              <EyeOutlined />
                                            </Button>
                                          </Tooltip>
                                          <Tooltip title={t('program.deleteSalaryCreator')}>
                                            <Button
                                              type="text"
                                              disabled={!roleUpdate || !field.canDelete}
                                              onClick={() => {
                                                Modal.confirm({
                                                  className: 't-pagetable_deleteLanguageModal',
                                                  autoFocusButton: 'cancel',
                                                  okText: t('system.ok'),
                                                  cancelText: t('system.cancel'),
                                                  cancelButtonProps: {
                                                    type: 'primary',
                                                  },
                                                  okButtonProps: {
                                                    type: 'default',
                                                  },
                                                  title: t('message.confirmDeleteRecord'),
                                                  onOk: () => {
                                                    remove(index);
                                                    onSubmit();
                                                  },
                                                });
                                              }}
                                            >
                                              <DeleteOutlined />
                                            </Button>
                                          </Tooltip>
                                        </Space>
                                      </td>
                                    </tr>
                                  ))
                                }
                                <tr>
                                  <td colSpan={2} style={{ textAlign: 'center', padding: '16px 0' }}>
                                    {
                                      permission && (
                                        <Button onClick={() => append(defaultValuePayroll)}>
                                          <PlusOutlined />
                                          <Typography.Text>Thêm</Typography.Text>
                                        </Button>
                                      )
                                    }
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            {
                              fieldState.error?.message && (
                                <span
                                  className="a-input_errorMessage"
                                >
                                  {fieldState.error?.message}
                                </span>
                              )
                            }
                          </>
                        )}
                      />
                    </Col>
                  </Card>
                </Space>
              </Col>
            </Row>
          </FormProvider>
        </div>
      </Spin>
    </div>
  );
};

export default ProgramDetail;
